import {
  JOURNAL_STATUS,
  SALES_INVOICE_STATUS,
  PURCHASE_BILL_STATUS,
  NORMAL_BALANCE,
} from "../types/accountsTypes";

export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export const formatCurrency = (amount, currency = "INR") => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

export const generateCode = (prefix) => {
  const year = new Date().getFullYear();
  const rand = Math.random().toString().slice(2, 6);
  return `${prefix}-${year}-${rand}`;
};

export const sumBy = (list = [], key) =>
  list.reduce((sum, item) => sum + Number(item[key] || 0), 0);

export const calculateInvoiceTotals = (items = []) => {
  const grossAmount = items.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.rate || 0),
    0,
  );

  const taxTotal = items.reduce((sum, item) => {
    const itemBase = Number(item.quantity || 0) * Number(item.rate || 0);
    const taxRate = Number(item.taxRate || 0);
    return sum + (itemBase * taxRate) / 100;
  }, 0);

  const grandTotal = grossAmount + taxTotal;

  return {
    grossAmount,
    taxableAmount: grossAmount,
    taxTotal,
    grandTotal,
  };
};

export const calculateBillTotals = (items = []) => {
  return calculateInvoiceTotals(items);
};

export const calculateJournalTotals = (lines = []) => {
  const totalDebit = lines.reduce(
    (sum, line) => sum + Number(line.debit || 0),
    0,
  );
  const totalCredit = lines.reduce(
    (sum, line) => sum + Number(line.credit || 0),
    0,
  );

  return {
    totalDebit,
    totalCredit,
    isBalanced:
      Number(totalDebit.toFixed(2)) === Number(totalCredit.toFixed(2)),
  };
};

export const validateJournalLines = (lines = []) => {
  if (!lines.length) {
    return { valid: false, message: "At least one journal line is required." };
  }

  const hasMissingAccount = lines.some((line) => !line.accountId);
  if (hasMissingAccount) {
    return { valid: false, message: "Every journal line needs an account." };
  }

  const invalidLine = lines.some((line) => {
    const debit = Number(line.debit || 0);
    const credit = Number(line.credit || 0);
    return (debit <= 0 && credit <= 0) || (debit > 0 && credit > 0);
  });

  if (invalidLine) {
    return {
      valid: false,
      message: "Each line must have either debit or credit, not both.",
    };
  }

  const totals = calculateJournalTotals(lines);
  if (!totals.isBalanced) {
    return { valid: false, message: "Journal is not balanced." };
  }

  return { valid: true, message: "" };
};

export const getJournalStatusDisplay = (status) => {
  const map = {
    [JOURNAL_STATUS.DRAFT]: { label: "Draft", color: "default" },
    [JOURNAL_STATUS.POSTED]: { label: "Posted", color: "success" },
    [JOURNAL_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
    [JOURNAL_STATUS.REVERSED]: { label: "Reversed", color: "warning" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getInvoiceStatusDisplay = (status) => {
  const map = {
    [SALES_INVOICE_STATUS.DRAFT]: { label: "Draft", color: "default" },
    [SALES_INVOICE_STATUS.SENT]: { label: "Sent", color: "info" },
    [SALES_INVOICE_STATUS.PARTIALLY_PAID]: {
      label: "Partially Paid",
      color: "warning",
    },
    [SALES_INVOICE_STATUS.PAID]: { label: "Paid", color: "success" },
    [SALES_INVOICE_STATUS.OVERDUE]: { label: "Overdue", color: "error" },
    [SALES_INVOICE_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
    [SALES_INVOICE_STATUS.VOID]: { label: "Void", color: "default" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getBillStatusDisplay = (status) => {
  const map = {
    [PURCHASE_BILL_STATUS.DRAFT]: { label: "Draft", color: "default" },
    [PURCHASE_BILL_STATUS.RECORDED]: { label: "Recorded", color: "info" },
    [PURCHASE_BILL_STATUS.PARTIALLY_PAID]: {
      label: "Partially Paid",
      color: "warning",
    },
    [PURCHASE_BILL_STATUS.PAID]: { label: "Paid", color: "success" },
    [PURCHASE_BILL_STATUS.OVERDUE]: { label: "Overdue", color: "error" },
    [PURCHASE_BILL_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
    [PURCHASE_BILL_STATUS.VOID]: { label: "Void", color: "default" },
  };
  return map[status] || { label: status, color: "default" };
};

export const deriveClosingBalanceType = (mainType, balance) => {
  if (balance === 0) return NORMAL_BALANCE.DEBIT;
  if (mainType === "asset" || mainType === "expense") {
    return balance >= 0 ? NORMAL_BALANCE.DEBIT : NORMAL_BALANCE.CREDIT;
  }
  return balance >= 0 ? NORMAL_BALANCE.CREDIT : NORMAL_BALANCE.DEBIT;
};
